/**
 * 상태·송신·정지·해제의 단일 소유자.
 *
 * UI 는 Serial 도 Python 도 직접 부르지 않는다. 반드시 이 엔진을 지난다.
 * 송신은 하나의 비동기 루프만 돌고, writer 도 transport 가 가진 하나뿐이다.
 * 모든 await 뒤에는 세션 번호를 확인해서, 늦게 끝난 작업이 새 세션의 상태를
 * 건드리지 못하게 한다.
 */
import type { CreateOptions } from "../python/bridge";
import type {
  BridgeSnapshot,
  DriveInput,
  EngineDiagnostics,
  EngineEvent,
  EngineState,
  EngineView,
  ProductInfo,
  StopConfirmation,
} from "../types";
import type { PortLike } from "../serial/transport";
import { SerialTransport } from "../serial/transport";
import { Waiter, withTimeout, type Clock } from "./clock";

/** 엔진이 쓰는 브리지의 최소 계약 — 테스트에서 가짜로 바꿔 끼운다. */
export interface BridgeLike {
  readonly product: ProductInfo;
  setInput(throttle: number, steering: number): void;
  setSpeedLimit(percent: number): number;
  setOutput(port: number, value: number): number;
  makeTx(): Uint8Array;
  makeStopTx(): Uint8Array;
  feed(chunk: Uint8Array): number;
  snapshot(): BridgeSnapshot;
  dispose(): void;
}

export type BridgeFactory = (productId: string, options: CreateOptions) => BridgeLike;

export interface EngineLimits {
  /** write 1회 감시 제한 */
  writeTimeoutMs: number;
  /** 정리 전체 감시 제한 */
  cleanupTimeoutMs: number;
  /** 마지막 입력 갱신이 이보다 오래되면 주행하지 않는다 */
  inputStaleMs: number;
  /** 송신 루프 재개 간격이 이보다 벌어지면 주행하지 않는다 */
  tickStaleMs: number;
  /** active 에서 유효 센서가 이 시간 없으면 정지 */
  sensorTimeoutMs: number;
  /** 연결 후 이 시간 안에 센서가 없으면 활성화를 막는다 */
  sensorStartupMs: number;
  stopRepeats: number;
  maxEvents: number;
  maxIntervals: number;
}

export const DEFAULT_LIMITS: EngineLimits = {
  writeTimeoutMs: 250,
  cleanupTimeoutMs: 3000,
  inputStaleMs: 250,
  tickStaleMs: 250,
  sensorTimeoutMs: 1000,
  sensorStartupMs: 2000,
  stopRepeats: 7,
  maxEvents: 200,
  maxIntervals: 256,
};

export interface EngineOptions {
  catalog: ProductInfo[];
  createBridge: BridgeFactory;
  clock: Clock;
  limits?: Partial<EngineLimits>;
  onChange?: (view: EngineView) => void;
}

interface Session {
  readonly id: number;
  readonly transport: SerialTransport;
  readonly bridge: BridgeLike;
  readonly product: ProductInfo;
  readonly intervalMs: number;
  closed: boolean;
  txLoop: Promise<void> | null;
  rxLoop: Promise<void> | null;
}

interface StopRequest {
  promise: Promise<void>;
  resolve: () => void;
  reject: (error: unknown) => void;
  reason: string;
  started: boolean;
  settled: boolean;
}

export class EngineError extends Error {
  readonly hint: string | null;
  constructor(message: string, hint: string | null = null) {
    super(message);
    this.name = "EngineError";
    this.hint = hint;
  }
}

export class WebCarEngine {
  readonly catalog: ProductInfo[];
  private readonly createBridge: BridgeFactory;
  private readonly clock: Clock;
  private readonly limits: EngineLimits;
  private readonly onChange: (view: EngineView) => void;
  private readonly waiter: Waiter;

  private state: EngineState = "loading";
  private message = "준비 중";
  private hint: string | null = null;

  private session: Session | null = null;
  private sessionCounter = 0;

  private productId: string;
  private maxSpeed: number;
  private leftSign = 1;
  private rightSign = 1;

  private input: DriveInput = { throttle: 0, steering: 0, at: 0 };
  private inputGenerationValue = 0;
  private sawRelease = true;

  private outputs: number[] = [];
  private snapshot: BridgeSnapshot | null = null;

  private connectedAt: number | null = null;
  private lastSensorAt: number | null = null;
  private lastWriteStartedAt: number | null = null;
  private intervals: number[] = [];
  private txWrites = 0;
  private rxFrames = 0;
  private errors = 0;
  private stopConfirmation: StopConfirmation = "none";
  private events: EngineEvent[] = [];

  private stopRequest: StopRequest | null = null;
  private closeIntent = false;
  private disconnectPromise: Promise<void> | null = null;
  private environment = { visible: true, focused: true };
  /** 정리 중 자기 자신을 기다려 멈추지 않도록 어느 루프 안인지 표시한다. */
  private inTxLoop = false;
  private inRxLoop = false;

  constructor(options: EngineOptions) {
    if (!options.catalog.length) throw new EngineError("제품 목록이 비어 있습니다");
    this.catalog = options.catalog;
    this.createBridge = options.createBridge;
    this.clock = options.clock;
    this.limits = { ...DEFAULT_LIMITS, ...options.limits };
    this.onChange = options.onChange ?? (() => {});
    this.waiter = new Waiter(this.clock);

    const first = this.catalog[0]!;
    this.productId = first.id;
    this.maxSpeed = first.web_initial_max_speed;
  }

  // ── 바깥에서 보는 상태 ──────────────────────────────────────────────
  get inputGeneration(): number {
    return this.inputGenerationValue;
  }

  get currentState(): EngineState {
    return this.state;
  }

  get selectedProduct(): ProductInfo {
    return this.catalog.find((item) => item.id === this.productId) ?? this.catalog[0]!;
  }

  get settings(): { productId: string; maxSpeed: number; leftSign: number; rightSign: number } {
    return {
      productId: this.productId,
      maxSpeed: this.maxSpeed,
      leftSign: this.leftSign,
      rightSign: this.rightSign,
    };
  }

  view(): EngineView {
    const blocked = this.activateBlockedReason();
    return {
      state: this.state,
      message: this.message,
      hint: this.hint,
      snapshot: this.snapshot,
      diagnostics: this.diagnostics(),
      events: this.events,
      canActivate: blocked === null,
      activateBlockedReason: blocked,
    };
  }

  private diagnostics(): EngineDiagnostics {
    const info = this.session ? this.session.transport.info() : { usbVendorId: null, usbProductId: null };
    return {
      product: this.session ? this.session.product : null,
      usbVendorId: info.usbVendorId,
      usbProductId: info.usbProductId,
      baud: this.session ? this.session.product.baud : null,
      targetIntervalMs: this.session ? this.session.intervalMs : null,
      intervals: this.intervals,
      txWrites: this.txWrites,
      rxFrames: this.rxFrames,
      errors: this.errors,
      sinceLastSensorMs:
        this.lastSensorAt === null ? null : Math.max(0, this.clock.now() - this.lastSensorAt),
      stopConfirmation: this.stopConfirmation,
    };
  }

  private notify(): void {
    this.onChange(this.view());
  }

  private setState(state: EngineState, message: string, hint: string | null = null): void {
    this.state = state;
    this.message = message;
    this.hint = hint;
    this.notify();
  }

  private log(level: EngineEvent["level"], message: string): void {
    this.events = [...this.events, { at: this.clock.now(), level, message }].slice(
      -this.limits.maxEvents,
    );
    if (level === "error") this.errors += 1;
  }

  // ── 설정 (연결 전에만 바꾸는 것들) ──────────────────────────────────
  markReady(): void {
    if (this.state !== "loading") return;
    this.setState("idle", "포트를 선택해 연결하세요");
  }

  markLoadFailed(message: string, hint: string | null): void {
    this.log("error", message);
    this.setState("error", message, hint);
  }

  setEnvironment(next: { visible: boolean; focused: boolean }): void {
    const wasUsable = this.environment.visible && this.environment.focused;
    this.environment = next;
    const usable = next.visible && next.focused;
    if (wasUsable && !usable && (this.state === "active" || this.state === "ready")) {
      // 보수적으로 비상정지와 같은 처리 — 탭을 벗어나면 못 멈출 수 있다.
      void this.requestStop(next.visible ? "창이 포커스를 잃음" : "탭이 숨겨짐").catch(() => {});
    }
    this.notify();
  }

  selectProduct(productId: string): void {
    if (this.session) throw new EngineError("연결 중에는 제품을 바꿀 수 없습니다. 먼저 해제하세요.");
    const product = this.catalog.find((item) => item.id === productId);
    if (!product) throw new EngineError(`모르는 제품 '${productId}'`);
    this.productId = productId;
    this.maxSpeed = product.web_initial_max_speed;
    this.notify();
  }

  setSigns(leftSign: number, rightSign: number): void {
    if (this.session) throw new EngineError("연결 중에는 모터 방향을 바꿀 수 없습니다.");
    if (![-1, 1].includes(leftSign) || ![-1, 1].includes(rightSign)) {
      throw new EngineError("모터 방향은 -1 또는 1 입니다");
    }
    this.leftSign = leftSign;
    this.rightSign = rightSign;
    this.notify();
  }

  setMaxSpeed(percent: number): number {
    if (!Number.isInteger(percent) || percent < 5 || percent > 100) {
      throw new EngineError("속도 상한은 5~100% 입니다");
    }
    this.maxSpeed = percent;
    if (this.session) this.session.bridge.setSpeedLimit(percent);
    this.refreshSnapshot();
    this.notify();
    return percent;
  }

  adjustMaxSpeed(delta: number): number {
    const next = Math.min(100, Math.max(5, this.maxSpeed + delta));
    return this.setMaxSpeed(next);
  }

  // ── 연결 ────────────────────────────────────────────────────────────
  /**
   * 포트 선택창을 여는 동안의 상태. 선택이 끝나기 전 중복 클릭을 막는다.
   * 선택창 자체는 사용자 동작 안에서 UI 가 직접 연다.
   */
  beginPortSelection(): void {
    if (this.state !== "idle") {
      throw new EngineError(`지금은 포트를 고를 수 없습니다 (${this.state})`);
    }
    this.setState("selecting", "포트 선택창에서 동글을 고르세요");
  }

  /** 선택창을 닫았는데 연결로 이어지지 않은 경우 — 취소는 오류가 아니다. */
  endPortSelection(): void {
    if (this.state === "selecting") {
      this.setState("idle", "포트를 선택해 연결하세요");
    }
  }

  async connect(port: PortLike): Promise<void> {
    if (this.state !== "idle" && this.state !== "selecting") {
      throw new EngineError(`지금은 연결할 수 없습니다 (${this.state})`);
    }
    const product = this.selectedProduct;
    const id = ++this.sessionCounter;
    this.setState("connecting", `${product.label} 연결 중`);
    this.resetSessionCounters();

    const transport = new SerialTransport(port);
    try {
      await transport.open(product.baud);
    } catch (error) {
      await transport.cleanup();
      this.log("error", `포트를 열지 못했습니다: ${String(error)}`);
      this.setState(
        "error",
        `포트를 열지 못했습니다: ${String(error)}`,
        "다른 프로그램이 포트를 쓰고 있는지, 드라이버와 권한이 맞는지 확인하세요.",
      );
      return;
    }

    // open 이 늦게 성공해도 그 사이 해제됐다면 ready 로 가지 않는다.
    if (id !== this.sessionCounter) {
      await this.abandonLateOpen(transport);
      return;
    }

    let bridge: BridgeLike;
    try {
      bridge = this.createBridge(product.id, {
        maxSpeed: this.maxSpeed,
        leftSign: this.leftSign,
        rightSign: this.rightSign,
      });
    } catch (error) {
      await transport.cleanup();
      this.log("error", `제품을 만들지 못했습니다: ${String(error)}`);
      this.setState("error", `제품을 만들지 못했습니다: ${String(error)}`, null);
      return;
    }

    const session: Session = {
      id,
      transport,
      bridge,
      product: bridge.product,
      intervalMs: Math.round(bridge.product.tx_interval * 1000),
      closed: false,
      txLoop: null,
      rxLoop: null,
    };
    this.session = session;
    this.outputs = new Array(bridge.product.out_ports).fill(0);
    this.connectedAt = this.clock.now();
    this.disconnectPromise = null;
    this.closeIntent = false;
    this.stopConfirmation = "none";

    session.rxLoop = this.rxLoop(session);
    session.txLoop = this.txLoop(session);

    // 초기 정지 7회를 끝낸 뒤에야 ready 의 주기 송신이 이어진다.
    try {
      await this.requestStop("연결 직후 정지");
    } catch {
      return; // 실패 처리는 정지 절차 안에서 끝났다
    }
    this.refreshSnapshot();
    this.notify();
  }

  /** 취소된 연결에서 늦게 열린 포트를 정리한다 — ready 로 가지 않는다. */
  private async abandonLateOpen(transport: SerialTransport): Promise<void> {
    await transport.cleanup();
    this.log("warn", "연결 도중 취소되어 포트를 정리했습니다");
    if (this.state === "connecting") {
      this.setState("idle", "포트를 선택해 연결하세요");
    }
  }

  private resetSessionCounters(): void {
    this.intervals = [];
    this.txWrites = 0;
    this.rxFrames = 0;
    this.lastSensorAt = null;
    this.lastWriteStartedAt = null;
    this.snapshot = null;
    this.input = { throttle: 0, steering: 0, at: this.clock.now() };
    this.inputGenerationValue += 1;
    this.sawRelease = true;
  }

  // ── 주행 ────────────────────────────────────────────────────────────
  activateBlockedReason(): string | null {
    if (this.state !== "ready") return "연결 후 정지 상태에서만 활성화할 수 있습니다";
    if (!this.environment.visible) return "이 탭을 보고 있어야 합니다";
    if (!this.environment.focused) return "이 창에 포커스가 있어야 합니다";
    if (this.stopRequest) return "정지 작업이 끝나기를 기다리는 중입니다";
    if (!this.sawRelease || this.input.throttle !== 0 || this.input.steering !== 0) {
      return "방향 입력을 모두 놓은 뒤에 활성화하세요";
    }
    const now = this.clock.now();
    if (this.lastSensorAt === null) {
      const waited = this.connectedAt === null ? 0 : now - this.connectedAt;
      if (waited > this.limits.sensorStartupMs) {
        return "센서 응답 없음: 제품·전원·동글 확인";
      }
      return "센서 응답을 기다리는 중입니다";
    }
    if (now - this.lastSensorAt > this.limits.sensorTimeoutMs) {
      return "센서 응답 없음: 제품·전원·동글 확인";
    }
    return null;
  }

  activate(): void {
    const blocked = this.activateBlockedReason();
    if (blocked) throw new EngineError(blocked);
    this.input = { throttle: 0, steering: 0, at: this.clock.now() };
    this.inputGenerationValue += 1;
    this.log("info", "주행 활성화");
    this.setState("active", "주행 활성화 — 방향키로 조종합니다");
    this.waiter.wake();
  }

  /**
   * 입력 갱신. 오래된 generation 은 버린다.
   * active 가 아니어도 받아 두어야 '모두 놓았다'를 알 수 있다.
   */
  setInput(throttle: number, steering: number, at: number, generation?: number): void {
    if (generation !== undefined && generation !== this.inputGenerationValue) return;
    if (typeof throttle === "boolean" || typeof steering === "boolean") {
      throw new EngineError("주행 입력은 숫자여야 합니다");
    }
    if (!Number.isFinite(throttle) || !Number.isFinite(steering)) {
      throw new EngineError("주행 입력은 유한한 숫자여야 합니다");
    }
    if (throttle < -1 || throttle > 1 || steering < -1 || steering > 1) {
      throw new EngineError("주행 입력은 -1~1 입니다");
    }
    this.input = { throttle, steering, at };
    if (throttle === 0 && steering === 0) this.sawRelease = true;
  }

  setOutput(port: number, value: number): void {
    const session = this.session;
    if (!session) throw new EngineError("연결된 제품이 없습니다");
    if (this.state !== "active") throw new EngineError("주행 활성화 상태에서만 출력을 바꿀 수 있습니다");
    session.bridge.setOutput(port, value);
    this.outputs[port - 1] = value;
    this.refreshSnapshot();
    this.notify();
  }

  outputValues(): number[] {
    return this.outputs;
  }

  // ── 정지 ────────────────────────────────────────────────────────────
  /** 여러 곳에서 불러도 진행 중인 같은 Promise 를 돌려준다. */
  requestStop(reason: string): Promise<void> {
    if (this.stopRequest) return this.stopRequest.promise;
    const session = this.session;
    if (!session) return Promise.resolve();
    if (session.closed) {
      return Promise.reject(new EngineError("연결이 이미 정리되어 정지를 보낼 수 없습니다"));
    }

    // 동기적으로 먼저 끈다 — 이 뒤에 들어오는 오래된 입력은 generation 으로 버린다.
    // 정지 시점에 이미 입력이 0 이었다면 따로 놓을 것이 없다. 잡고 있었다면
    // 실제로 놓는 것을 본 뒤에야 다시 활성화할 수 있다.
    const wasIdle = this.input.throttle === 0 && this.input.steering === 0;
    this.input = { throttle: 0, steering: 0, at: this.clock.now() };
    this.inputGenerationValue += 1;
    this.sawRelease = wasIdle;
    this.outputs = this.outputs.map(() => 0);

    let resolve!: () => void;
    let reject!: (error: unknown) => void;
    const promise = new Promise<void>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    // 아무도 기다리지 않는 정지 실패가 처리되지 않은 거부로 남지 않게 한다.
    promise.catch(() => {});
    this.stopRequest = { promise, resolve, reject, reason, started: false, settled: false };
    this.log("info", `정지 요청: ${reason}`);
    this.setState("stopping", `정지 중 (${reason})`);
    this.waiter.wake();
    return promise;
  }

  emergencyStop(reason = "비상정지"): Promise<void> {
    return this.requestStop(reason);
  }

  private settleStop(error?: unknown): void {
    const request = this.stopRequest;
    if (!request || request.settled) return;
    request.settled = true;
    this.stopRequest = null;
    if (error) request.reject(error);
    else request.resolve();
  }

  private async runStopSequence(session: Session): Promise<void> {
    const request = this.stopRequest;
    if (!request) return;
    request.started = true;
    this.setState("stopping", `정지 중 (${request.reason})`);

    for (let index = 0; index < this.limits.stopRepeats; index += 1) {
      if (!this.isCurrent(session)) {
        this.settleStop(new EngineError("세션이 끝나 정지를 마치지 못했습니다"));
        return;
      }
      let packet: Uint8Array;
      try {
        packet = session.bridge.makeStopTx();
      } catch (error) {
        this.stopConfirmation = "unconfirmed";
        this.settleStop(error);
        await this.failSession(session, `정지 패킷을 만들지 못했습니다: ${String(error)}`, null);
        return;
      }

      const ok = await this.writeOnce(session, packet);
      if (!this.isCurrent(session)) {
        this.settleStop(new EngineError("세션이 끝나 정지를 마치지 못했습니다"));
        return;
      }
      if (!ok) {
        this.stopConfirmation = "unconfirmed";
        const error = new EngineError("정지 전송 확인 불가");
        this.settleStop(error);
        await this.failSession(
          session,
          "정지 전송 확인 불가 — 장치 전원과 연결 상태를 확인하세요",
          "바퀴가 계속 돈다면 전원을 끄세요. 통신 성공은 실제 정지를 뜻하지 않습니다.",
        );
        return;
      }
      if (index < this.limits.stopRepeats - 1) {
        await this.waiter.wait(session.intervalMs);
      }
    }

    this.stopConfirmation = "sent";
    this.outputs = this.outputs.map(() => 0);
    this.refreshSnapshot();
    this.log("info", "정지 명령 전송 완료 (7회)");
    if (!this.closeIntent) {
      this.setState("ready", "정지 명령 전송 완료 — 주행 활성화를 누르면 조종합니다");
    }
    this.settleStop();
  }

  // ── 송신 루프 ───────────────────────────────────────────────────────
  private isCurrent(session: Session): boolean {
    return this.session === session && !session.closed;
  }

  /** true 면 성공. timeout·오류면 false 이며 세션은 정리 대상이 된다. */
  private async writeOnce(session: Session, packet: Uint8Array): Promise<boolean> {
    const startedAt = this.clock.now();
    let result;
    try {
      result = await withTimeout(
        this.clock,
        session.transport.write(packet),
        this.limits.writeTimeoutMs,
        (late) => {
          // 늦게 끝난 write 는 기록만 한다 — 상태를 바꾸지 못한다.
          this.log(
            "warn",
            late.ok ? "감시 제한을 넘긴 송신이 나중에 끝났습니다" : "감시 제한을 넘긴 송신이 실패로 끝났습니다",
          );
        },
      );
    } catch (error) {
      if (this.isCurrent(session)) {
        await this.failSession(
          session,
          `송신 실패: ${String(error)}`,
          "동글이 빠졌거나 포트가 닫혔는지 확인하세요.",
        );
      }
      return false;
    }
    if (!this.isCurrent(session)) return false;
    if (result.timedOut) {
      this.log("error", `송신이 ${this.limits.writeTimeoutMs}ms 안에 끝나지 않았습니다`);
      return false;
    }

    this.txWrites += 1;
    if (this.lastWriteStartedAt !== null) {
      this.intervals = [...this.intervals, startedAt - this.lastWriteStartedAt].slice(
        -this.limits.maxIntervals,
      );
    }
    this.lastWriteStartedAt = startedAt;
    return true;
  }

  private async txLoop(session: Session): Promise<void> {
    this.inTxLoop = true;
    try {
      await this.txLoopBody(session);
    } finally {
      this.inTxLoop = false;
    }
  }

  private async txLoopBody(session: Session): Promise<void> {
    let next = this.clock.now();
    let lastTickAt = this.clock.now();

    while (this.isCurrent(session)) {
      if (this.stopRequest && !this.stopRequest.started) {
        await this.runStopSequence(session);
        if (!this.isCurrent(session)) return;
        const resumed = this.clock.now();
        next = resumed + session.intervalMs;
        lastTickAt = resumed;
        continue;
      }

      const now = this.clock.now();
      if (now < next) {
        await this.waiter.wait(next - now);
        continue;
      }

      const gap = now - lastTickAt;
      lastTickAt = now;

      if (this.state === "active") {
        const reason = this.driveBlockedReason(now, gap);
        if (reason) {
          void this.requestStop(reason);
          continue;
        }
      }

      let packet: Uint8Array;
      try {
        // 보낼 차례에만 만든다 — 미리 만들어 두었다가 버리지 않는다.
        if (this.state === "active") {
          session.bridge.setInput(this.input.throttle, this.input.steering);
        } else {
          session.bridge.setInput(0, 0);
        }
        packet = session.bridge.makeTx();
      } catch (error) {
        await this.failSession(session, `패킷을 만들지 못했습니다: ${String(error)}`, null);
        return;
      }

      const ok = await this.writeOnce(session, packet);
      if (!this.isCurrent(session)) return;
      if (!ok) {
        this.stopConfirmation = "unconfirmed";
        await this.failSession(
          session,
          "송신이 끝나지 않아 연결을 정리했습니다 — 정지 전송 확인 불가",
          "바퀴가 계속 돈다면 전원을 끄세요.",
        );
        return;
      }

      this.refreshSnapshot();
      this.notify();

      const after = this.clock.now();
      next += session.intervalMs;
      if (next <= after) next = after + session.intervalMs; // 지각분을 몰아 보내지 않는다
    }
  }

  private driveBlockedReason(now: number, gap: number): string | null {
    if (gap > this.limits.tickStaleMs) return "송신 주기 지연";
    if (now - this.input.at > this.limits.inputStaleMs) return "입력 갱신 지연";
    if (this.lastSensorAt === null) return "센서 응답 없음";
    if (now - this.lastSensorAt > this.limits.sensorTimeoutMs) return "센서 응답 없음";
    if (!this.environment.visible) return "탭이 숨겨짐";
    if (!this.environment.focused) return "창이 포커스를 잃음";
    return null;
  }

  // ── 수신 루프 ───────────────────────────────────────────────────────
  private async rxLoop(session: Session): Promise<void> {
    this.inRxLoop = true;
    try {
      await this.rxLoopBody(session);
    } finally {
      this.inRxLoop = false;
    }
  }

  private async rxLoopBody(session: Session): Promise<void> {
    while (this.isCurrent(session)) {
      let chunk;
      try {
        chunk = await session.transport.read();
      } catch (error) {
        if (this.isCurrent(session)) {
          await this.failSession(
            session,
            `수신 오류: ${String(error)}`,
            "동글과 제품 전원을 확인한 뒤 다시 연결하세요.",
          );
        }
        return;
      }
      if (this.session !== session) return; // 지난 세션의 결과는 버린다
      if (chunk.done) {
        if (!session.closed) {
          await this.failSession(
            session,
            "수신 스트림이 끝났습니다",
            "동글이 빠졌는지 확인한 뒤 다시 연결하세요.",
          );
        }
        return;
      }
      if (!chunk.value || chunk.value.length === 0) continue;

      try {
        const frames = session.bridge.feed(chunk.value);
        if (frames > 0) {
          this.rxFrames += frames;
          this.lastSensorAt = this.clock.now();
        }
      } catch (error) {
        await this.failSession(session, `수신 해석 오류: ${String(error)}`, null);
        return;
      }
      this.refreshSnapshot();
      this.notify();
    }
  }

  private refreshSnapshot(): void {
    if (!this.session) return;
    try {
      this.snapshot = this.session.bridge.snapshot();
    } catch {
      // 스냅샷 실패로 주행 상태를 바꾸지 않는다 — 화면만 이전 값을 유지한다.
    }
  }

  // ── 해제·오류 정리 ──────────────────────────────────────────────────
  disconnect(): Promise<void> {
    if (this.disconnectPromise) return this.disconnectPromise;
    const session = this.session;
    if (!session) {
      // 아직 세션이 없어도 진행 중인 연결은 무효로 만든다 — 늦게 열린 포트가
      // ready 로 이어지지 않게 하기 위해서다.
      this.sessionCounter += 1;
      if (this.state !== "error") this.setState("idle", "포트를 선택해 연결하세요");
      return Promise.resolve();
    }
    this.disconnectPromise = this.runDisconnect(session);
    return this.disconnectPromise;
  }

  private async runDisconnect(session: Session): Promise<void> {
    this.closeIntent = true;
    this.log("info", "해제 요청");
    try {
      await this.requestStop("해제");
    } catch {
      // 정지 실패는 failSession 이 이미 error 로 남겼다. 정리는 계속한다.
    }
    if (this.session === session) {
      await this.teardown(session, "idle", "포트를 선택해 연결하세요", null);
    }
    this.disconnectPromise = null;
  }

  /** 오류 경로 — 정상 정지를 보장할 수 없으므로 성공으로 위장하지 않는다. */
  private async failSession(session: Session, message: string, hint: string | null): Promise<void> {
    if (!this.isCurrent(session)) return;
    this.log("error", message);
    this.settleStop(new EngineError(message));
    await this.teardown(session, "error", message, hint);
  }

  private async teardown(
    session: Session,
    finalState: EngineState,
    message: string,
    hint: string | null,
  ): Promise<void> {
    if (this.session !== session) return;
    this.setState("disconnecting", "연결 정리 중");
    session.closed = true;
    this.waiter.wake();

    const errors: Error[] = [];
    const deadline = withTimeout(
      this.clock,
      this.closeResources(session, errors),
      this.limits.cleanupTimeoutMs,
    );
    const outcome = await deadline;

    this.session = null;
    this.connectedAt = null;
    this.settleStop(new EngineError("연결이 정리되었습니다"));

    if (outcome.timedOut) {
      this.log("error", "정리 시간이 초과되었습니다");
      this.setState(
        "error",
        "포트 정리 실패 — 장치 전원과 연결 상태 확인 후 페이지를 다시 여세요",
        "이 엔진에서는 새 연결을 열지 않습니다.",
      );
      return;
    }
    if (errors.length) {
      for (const error of errors) this.log("error", error.message);
      this.setState(
        "error",
        "포트 정리 실패 — 장치 전원과 연결 상태 확인 후 페이지를 다시 여세요",
        errors.map((error) => error.message).join(" / "),
      );
      return;
    }
    this.setState(finalState, message, hint);
  }

  /** 정리 순서 — TX 종료 → 수신 취소 → RX 종료 → 잠금 해제·포트 닫기 → 브리지 폐기. */
  private async closeResources(session: Session, errors: Error[]): Promise<void> {
    // 루프가 스스로 정리를 부른 경우에는 자기 자신을 기다리지 않는다.
    if (!this.inTxLoop) {
      try {
        if (session.txLoop) await session.txLoop;
      } catch (error) {
        errors.push(new Error(`송신 루프 종료: ${String(error)}`));
      }
    }
    try {
      await session.transport.cancelRead();
    } catch (error) {
      errors.push(new Error(`수신 취소: ${String(error)}`));
    }
    if (!this.inRxLoop) {
      try {
        if (session.rxLoop) await session.rxLoop;
      } catch (error) {
        errors.push(new Error(`수신 루프 종료: ${String(error)}`));
      }
    }
    errors.push(...(await session.transport.cleanup()));
    try {
      session.bridge.dispose();
    } catch (error) {
      errors.push(new Error(`브리지 폐기: ${String(error)}`));
    }
  }

  /** navigator.serial 의 disconnect 이벤트 — 현재 포트일 때만 같은 경로로. */
  async handlePortDisconnected(port: PortLike): Promise<void> {
    const session = this.session;
    if (!session || !session.transport.owns(port)) return;
    this.stopConfirmation = "unconfirmed";
    await this.failSession(
      session,
      "장치 연결이 끊어졌습니다 — 정지 전송 확인 불가",
      "바퀴가 계속 돈다면 전원을 끄세요. 다시 꽂은 뒤 새로 연결하세요.",
    );
  }

  /** 페이지가 닫히는 중 — 최선의 정지 요청만 한다. 완료는 보장하지 않는다. */
  bestEffortStop(): void {
    if (!this.session) return;
    void this.requestStop("페이지 종료").catch(() => {});
  }
}
